import classNames from "classnames";
import DOMPurify from "dompurify";
import React, { memo } from "react";
import { Svg, SvgSize } from "../../../../Shared/Assets/Svg/React";
import { Button } from "../../../../Shared/Buttons/React/button";
import { ButtonColor } from "../../../../Shared/Buttons/React/buttonColor";
import { ButtonSize } from "../../../../Shared/Buttons/React/buttonSize";
import { CollapsibleSection } from "../../../../Shared/Collapsible/React";
import { useTranslationContext } from "../../context";

interface ProvetImportantInformationStepProps {
  emergencyClinicsUrl?: string;
}

/**
 * Displays the important information step in the Provet booking flow.
 * Shows emergency clinic information and call us information before booking begins.
 */
export const ProvetImportantInformationStep = memo(
  function ProvetImportantInformationStep({
    emergencyClinicsUrl
  }: ProvetImportantInformationStepProps) {
    const translationContext = useTranslationContext();
    const translations = translationContext?.translations;

    return (
      <div className="space-y-xs lg:hidden">
        {/* Emergency Clinics Information Collapsible */}
        {!!translations?.emergencyClinicsInformation && (
          <CollapsibleSection
            variant="emergency"
            labelClickToOpen={translations?.emergenciesHeading || "Emergency Clinics"}
            labelClickToClose={translations?.emergenciesHeading || "Emergency Clinics"}
          >
            <div className="flex flex-col gap-xs">
              <div
                className={classNames(
                  "richtext gap-xl text-t-sm text-t-pink font-normal px-xxs",
                  "[&_a]:font-t-bold [&_a]:underline [&>p_a]:decoration-pink-1000 [&_a]:text-dark-blue [&_a]:underline-offset-[0.35rem]",
                  "[&_a]:focus-outline",
                  "[&_ul:not(.list--tiny-checkmark)_li]:before:bg-dark-blue"
                )}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(translations?.emergencyClinicsInformation)
                }}
              />

              {emergencyClinicsUrl && (
                <Button
                  additionalClass="self-start"
                  color={ButtonColor.DarkBlue}
                  data-emergency-button
                  href={emergencyClinicsUrl}
                  size={ButtonSize.SM}
                >
                  {translations?.emergencyClinicsButtonLabel}

                  <Svg href="/svg/system/arrow-right.svg" size={SvgSize.MD} />
                </Button>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Call Us Information Collapsible */}
        {!!translations?.callUsInformation && (
          <CollapsibleSection
            variant="information"
            labelClickToOpen={translations?.informationBoxHeading || "Call Us"}
            labelClickToClose={translations?.informationBoxHeading || "Call Us"}
          >
            <div
              className={classNames(
                "richtext gap-xl text-t-sm text-t-neutral-800 font-normal px-xxs",
                "[&_a]:font-t-bold [&_a]:underline [&_a]:decoration-turquoise-800 [&_a]:text-dark-blue [&_a]:underline-offset-[0.35rem]",
                "[&_a]:focus-outline",
                "[&_ul:not(.list--tiny-checkmark)_li]:before:bg-dark-blue"
              )}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(translations?.callUsInformation)
              }}
            />
          </CollapsibleSection>
        )}
      </div>
    );
  }
);

export default ProvetImportantInformationStep;
